import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown, X } from 'lucide-react';
import { catalogColors } from './catalogColors';
import { CatalogFilterOptions } from '@/hooks/useCatalog';
import { sourceLabel } from './sourceLabels';

export interface FilterChip {
  dim: keyof CatalogFilterOptions;
  label: string;
}

interface CatalogFilterChipsProps {
  chips: FilterChip[];
  activeFilters: Record<string, string>;
  options: CatalogFilterOptions | undefined;
  onSet: (dim: string, value: string) => void;
  onClear: (dim: string) => void;
  onClearAll?: () => void;
}

// Per-dimension value formatter — source uses canonical labels, others render raw.
function formatValue(dim: string, value: string): string {
  if (dim === 'source') return sourceLabel(value);
  return value;
}

export function CatalogFilterChips({
  chips,
  activeFilters,
  options,
  onSet,
  onClear,
  onClearAll,
}: CatalogFilterChipsProps) {
  const activeCount = chips.reduce(
    (acc, c) => acc + (activeFilters[c.dim] ? 1 : 0),
    0,
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {chips.map(({ dim, label }) => {
        const active = activeFilters[dim];
        if (active) {
          return (
            <Badge
              key={dim}
              variant="secondary"
              className="flex items-center gap-1 text-xs cursor-pointer h-7 px-2 hover:bg-muted/80"
              style={{ backgroundColor: catalogColors.accentTeal + '20', color: catalogColors.accentTeal }}
              onClick={() => onClear(dim)}
            >
              <span className="font-medium">{label}:</span>
              <span className="truncate max-w-[160px]">{formatValue(dim, active)}</span>
              <X className="h-2.5 w-2.5 shrink-0" />
            </Badge>
          );
        }
        return (
          <DimensionPopover
            key={dim}
            dim={dim}
            label={label}
            options={options?.[dim] ?? []}
            onSelect={(value) => onSet(dim, value)}
          />
        );
      })}

      {activeCount > 0 && (
        <span className="text-xs text-muted-foreground inline-flex items-center gap-2">
          {activeCount} filter{activeCount === 1 ? '' : 's'} active
          {onClearAll && (
            <button
              type="button"
              className="underline hover:text-foreground"
              onClick={onClearAll}
            >
              Clear all
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function DimensionPopover({
  dim,
  label,
  options,
  onSelect,
}: {
  dim: string;
  label: string;
  options: string[];
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
        >
          {label}
          <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-1 max-h-[320px] overflow-y-auto">
        {options.length === 0 ? (
          <div className="text-xs text-muted-foreground p-2 text-center">No values</div>
        ) : (
          options.map(value => (
            <button
              key={value}
              type="button"
              onClick={() => { onSelect(value); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-muted"
            >
              {formatValue(dim, value)}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}
